/* eslint-disable @typescript-eslint/no-namespace */
import { type Brand, make } from "ts-brand";
import { ServiceDefinition } from "./ServiceDefinition";
import { ServiceProvider } from "./ServiceProvider";

/**
 * A custom service definition is a service definition that is created by a
 * service provider to offer a service to a customer. It can be created by
 * customizing a standard service definition, or by creating a new service
 * definition from scratch.
 *
 * Custom service definitions are managed in the service definition repository.
 *
 * @see ServiceDefinitionRepository
 */
export interface CustomServiceDefinition extends Omit<ServiceDefinition, "id"> {
    /**
     * The id of the service definition.
     */
    id: CustomServiceDefinition.Id;
}

export namespace CustomServiceDefinition {
    /**
     * The id to identify the service.
     *
     * Must be a non-empty ID string.
     * Format: {providerId}:{serviceId}:{shortId} where shortId is 8 alphanumeric characters.
     */
    export type Id = Brand<string, CustomServiceDefinition>;

    /**
     * Creates a new service definition ID.
     *
     * @param value The value to create the ID from.
     * @returns The new service definition ID.
     * @throws An error if the ID is empty or invalid format.
     */
    export const Id = make<Id>(value => {
        if (typeof value !== "string") {
            throw new Error("CustomServiceDefinition: Property 'id' must be a string.");
        }

        if (!value || value.trim() === "") {
            throw new Error("CustomServiceDefinition: Property 'id' cannot be empty.");
        }

        const parts = value.split(":");
        if (parts.length !== 3) {
            throw new Error(
                "CustomServiceDefinition: Property 'id' must be in format 'providerId:serviceId:shortId'."
            );
        }

        // Validate short ID format (8 lowercase alphanumeric)
        const shortId = parts[2];
        if (!shortId || !/^[a-z0-9]{8}$/.test(shortId)) {
            throw new Error("CustomServiceDefinition: Short ID must be 8 lowercase alphanumeric characters.");
        }

        return value;
    });

    /**
     * Generates a short ID (8 characters) from UUID.
     *
     * @returns An 8-character lowercase alphanumeric ID.
     */
    const generateShortId = (): string => {
        return crypto.randomUUID().replace(/-/g, "").substring(0, 8).toLowerCase();
    };

    /**
     * Creates a new custom service definition ID with automatic short ID generation.
     *
     * @param serviceId The service ID.
     * @param providerId The provider ID.
     * @returns The new custom service definition ID.
     * @throws An error if parameters are invalid.
     */
    export const createId = (serviceId: ServiceDefinition.Id, providerId: ServiceProvider.Id): Id => {
        if (!providerId) {
            throw new Error("CustomServiceDefinition: Provider ID is required.");
        }
        if (!serviceId) {
            throw new Error("CustomServiceDefinition: Service ID is required.");
        }

        return Id(`${providerId}:${serviceId}:${generateShortId()}`);
    };

    /**
     * Parses a custom service definition ID into its components.
     *
     * @param id The custom service definition ID to parse.
     * @returns Object containing providerId, serviceId, and shortId.
     */
    export const parse = (
        id: Id
    ): {
        providerId: ServiceProvider.Id;
        serviceId: ServiceDefinition.Id;
        shortId: string;
    } => {
        if (!isCustomServiceId(id)) {
            throw new Error(
                "CustomServiceDefinition: Invalid ID format. Expected 'providerId:serviceId:shortId'."
            );
        }
        const parts = id.split(":");
        return {
            providerId: ServiceProvider.Id(parts[0]!),
            serviceId: ServiceDefinition.Id(parts[1]!),
            shortId: parts[2]!,
        };
    };

    export const isCustomServiceId = (value: unknown): value is Id => {
        if (typeof value !== "string") {
            return false;
        }
        const parts = value.split(":");
        return parts.length === 3 && !!parts[0] && !!parts[1] && !!parts[2];
    };

    export const isValid = (value: unknown): value is CustomServiceDefinition => {
        return (
            typeof value === "object" &&
            value !== null &&
            "id" in value &&
            "name" in value &&
            "parameterGroups" in value &&
            isCustomServiceId(value.id)
        );
    };
}
